import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule} from '@angular/core';
import { RouteReuseStrategy, Router } from "@angular/router";
import { MenuRouteReuseStrategy } from './menudishes/menu-route-reuse.strategy';
import { AppRoutingModule, RoutingComponent } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialogModule } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TraceService, createErrorHandler } from '@sentry/angular';
import { SidenavComponent } from './common/sidenav/sidenav.component';
import { ModernSidenavComponent } from './common/sidenav/components/modern-sidenav/modern-sidenav.component';
import { SidenavGroupComponent } from './common/sidenav/components/sidenav-group/sidenav-group.component';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NZ_I18N, en_US, NZ_DATE_LOCALE } from 'ng-zorro-antd/i18n';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { OrderDetailsComponent } from './server/components/order-details/order-details.component';
import { TotalAmountComponent } from './server/components/total-amount/total-amount.component';
import { OrderListComponent } from './server/components/order-list/order-list.component';
import { TableServiceComponent } from './table-service/table-service.component';
import { PaymentPendingComponent } from './modals/payment-pending/payment-pending.component';
import { PrintOrderdetailsComponent } from './modals/print-orderdetails/print-orderdetails.component';
import { RiderTrackComponent } from './modals/rider-track/rider-track.component';
import { RouterModule } from '@angular/router';
import { VoidDishComponent } from './modals/void-dish/void-dish.component';
import { PaymentOptionsComponent } from './common/components/payment-options/payment-options.component';
import { SharedModule } from './shared/shared.module';
import { InterceptorService } from './common/services/intercepter.service';
import { ClockinOutComponenet } from './staff-center/modals/clockin-out/clockin-out.component';
import { RegisterServicesComponent } from './register-services/register-services.component';
import { ThemeModule } from './common/theme/theme.module';
import { AuthGuard } from './common/services/auth-guard.service';
import { AuthService } from './common/services/auth.service';
import { ActionPromptDialogComponent } from './common/dialog/action-prompt-dialog/action-prompt-dialog.component';
import { LocalStorageService } from './common/services/localStorage.service';
import { RestaurantDetailsService } from './common/services/restaurant-details.service';
import { RESTAURANT_DETAILS_SERVICE } from './common/services/restaurant-details.token';
import { TableRouteGuard } from './tables-layout/table-route.guard';
import { OpenDrawerComponent } from './common/components/open-drawer/open-drawer.component';
import { UpdateCashComponent } from './common/components/update-cash/update-cash.component';
import { DrawerKickComponent } from './common/components/drawer-kick/drawer-kick.component';
import { CashierConfirmationComponent } from './common/components/cashier-confirmation/cashier-confirmation.component';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { enUS } from 'date-fns/locale';
import { NgxMaskModule } from 'ngx-mask';
import { PublishModalComponent } from './modals/publish-modal/publish-modal.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AddLoyaltyCashbackComponent } from './common/components/add-loyalty-cashback/add-loyalty-cashback.component';
import { EeValueComponent } from './common/components/ee-value/ee-value.component';
import { AddVoidedItemComponent } from './modals/add-voided-item/add-voided-item.component';
import { catchError } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ScanQrAdComponent } from './common/components/scan-qr-ad/scan-qr-ad.component';
import { NewFeatureAdComponent } from './common/components/new-feature-ad/new-feature-ad.component';
import { SubscriptionBannerComponent } from './common/components/subscription-banner/subscription-banner.component';
import { LibraryModule } from './library/library.module';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { HqBannerComponent } from './common/components/hq-banner/hq-banner.component';
import { LhdnModalComponent } from './common/components/lhdn-modal/lhdn-modal.component';
import { DowntimeWarningComponent } from './common/components/downtime-warning/downtime-warning.component';
import { SupportOperatingHoursNoticeComponent } from './common/components/support-operating-hours-notice/support-operating-hours-notice.component';
import { AppUpdateNoticeComponent } from './common/components/app-update-notice/app-update-notice.component';

export class CustomTranslateHttpLoader extends TranslateHttpLoader {
  constructor(http: HttpClient, prefix: string, suffix: string) {
    super(http, prefix, suffix);
  }

  getTranslation(lang: string): Observable<any> {
    return super.getTranslation(lang).pipe(
      catchError((error: any) => {
        console.error('Translation file loading error:', error);
        const fallbackLang = 'en_US'; // Set a fallback language code (e.g., 'en' for English)
        return import(`../assets/locale/${fallbackLang}.json`);
      })
    );
  }
}

export function HttpLoaderFactory(http: HttpClient): CustomTranslateHttpLoader {
  return new CustomTranslateHttpLoader(http, './assets/locale/', '.json');
}
@NgModule({
  declarations: [
    AppComponent,
    RoutingComponent,
    SidenavComponent,
    ModernSidenavComponent,
    SidenavGroupComponent,
    OrderDetailsComponent,
    TotalAmountComponent,
    OrderListComponent,
    TableServiceComponent,
    PaymentPendingComponent,
    PrintOrderdetailsComponent,
    RiderTrackComponent,
    VoidDishComponent,
    PaymentOptionsComponent,
    ClockinOutComponenet,
    RegisterServicesComponent,
    ActionPromptDialogComponent,
    OpenDrawerComponent,
    UpdateCashComponent,
    DrawerKickComponent,
    CashierConfirmationComponent,
    PublishModalComponent,
    AddLoyaltyCashbackComponent,
    EeValueComponent,
    AddVoidedItemComponent,
    ScanQrAdComponent,
    NewFeatureAdComponent,
    SubscriptionBannerComponent,
    HqBannerComponent,
    LhdnModalComponent,
    DowntimeWarningComponent,
    SupportOperatingHoursNoticeComponent,
    AppUpdateNoticeComponent,
    ],
  imports: [
    BrowserModule,
    MatDialogModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTabsModule,
    MatInputModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    NzDatePickerModule,
    NzMessageModule,
    NzCarouselModule,
    NzUploadModule,
    NzBadgeModule,
    NzIconModule,
    NgMultiSelectDropDownModule.forRoot(),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      isolate: true,
    }),
    MatSidenavModule,
    RouterModule,
    SharedModule,
    ThemeModule,
    NgIdleKeepaliveModule.forRoot(),
    NgxMaskModule.forRoot(),
    InfiniteScrollModule,
    LibraryModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorService,
      multi: true,
    },
    AuthGuard,
    AuthService,
    LocalStorageService,
    {
      provide: RESTAURANT_DETAILS_SERVICE,
      useExisting: RestaurantDetailsService,
    },
    { provide: 'WINDOW', useValue: window },
    TableRouteGuard,
    { provide: NZ_DATE_LOCALE, useValue: enUS },
    { provide: RouteReuseStrategy, useClass: MenuRouteReuseStrategy },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(trace: TraceService) {}
}
